import { useState, useMemo } from "react";
import { Plus, Fuel, Loader2, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { useFuelLogs } from "../hooks/useFuelLogs";
import FuelLogFormModal from "./FuelLogFormModal";

function fmtDateTime(iso) {
  return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// Mileage (km/l) for a fill-up is derived from the distance since the *previous*
// fill-up of the same vehicle, divided by the liters put in this time.
function computeMileageMap(logs) {
  const byVehicle = {};
  logs.forEach(l => { (byVehicle[l.vehicleId] ||= []).push(l); });

  const mileage = {};
  Object.values(byVehicle).forEach(vehicleLogs => {
    const asc = [...vehicleLogs].sort((a, b) => new Date(a.filledAt) - new Date(b.filledAt));
    for (let i = 1; i < asc.length; i++) {
      const prev = asc[i - 1];
      const cur  = asc[i];
      if (prev.odometerKm != null && cur.odometerKm != null && cur.liters > 0) {
        const distance = cur.odometerKm - prev.odometerKm;
        if (distance > 0) mileage[cur.id] = distance / cur.liters;
      }
    }
  });
  return mileage;
}

export default function FuelLogPanel({ vehicles }) {
  const { logs, loading, addLog, removeLog } = useFuelLogs();
  const [showForm, setShowForm] = useState(false);

  const mileageMap = useMemo(() => computeMileageMap(logs), [logs]);

  function vehicleLabel(vehicleId) {
    const v = vehicles.find(v => v.id === vehicleId);
    return v ? (v.nickname || v.plateNumber) : "Unknown vehicle";
  }

  async function handleDelete(log) {
    if (!window.confirm(`Remove this fuel log?`)) return;
    try {
      await removeLog(log.id);
      toast.success("Fuel log removed");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to remove fuel log");
    }
  }

  return (
    <main style={{ flex: 1, overflow: "auto", background: "#F9FAFB" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "28px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#111827" }}>Fuel Logs</div>
            <div style={{ fontSize: 13, color: "#6B7280", marginTop: 2 }}>Track fill-ups, cost, and mileage per vehicle</div>
          </div>
          <button
            onClick={() => setShowForm(true)}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "#4F46E5", border: "none", borderRadius: 8,
              padding: "0 16px", height: 38, cursor: "pointer",
              fontSize: 13.5, fontWeight: 700, color: "#fff",
            }}
          >
            <Plus size={15} /> Add Fuel Log
          </button>
        </div>

        {loading && (
          <div style={{ padding: 60, textAlign: "center", color: "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> Loading fuel logs…
          </div>
        )}

        {!loading && logs.length === 0 && (
          <div style={{
            background: "#fff", border: "1px dashed #D1D5DB", borderRadius: 14,
            padding: "48px 24px", textAlign: "center",
          }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 6 }}>No fuel logs yet</div>
            <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 18 }}>Add your first fill-up to start tracking fuel cost and mileage.</div>
            <button
              onClick={() => setShowForm(true)}
              style={{ background: "#4F46E5", border: "none", borderRadius: 8, padding: "0 18px", height: 38, color: "#fff", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}
            >
              <Plus size={14} style={{ marginRight: 6, verticalAlign: -2 }} /> Add Fuel Log
            </button>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {logs.map(log => {
            const mileage = mileageMap[log.id];
            return (
              <div key={log.id} style={{
                background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12,
                padding: "16px 18px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                  background: "#EEF2FF", color: "#4F46E5",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Fuel size={18} />
                </div>

                <div style={{ flex: "1 1 260px", minWidth: 200 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: "#111827" }}>
                    {vehicleLabel(log.vehicleId)}
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 4, flexWrap: "wrap", fontSize: 12, color: "#6B7280" }}>
                    <span>{fmtDateTime(log.filledAt)}</span>
                    {log.liters != null && <span>{log.liters} L</span>}
                    {log.cost != null && <span>₹{log.cost.toLocaleString("en-IN")}</span>}
                    {log.odometerKm != null && <span>{log.odometerKm.toLocaleString("en-IN")} km</span>}
                    {log.fuelStation && <span>{log.fuelStation}</span>}
                  </div>
                  {mileage != null && (
                    <span style={{
                      display: "inline-block", marginTop: 6, fontSize: 10.5, fontWeight: 700,
                      color: "#2563EB", background: "#EFF6FF", borderRadius: 99, padding: "2px 9px",
                    }}>
                      {mileage.toFixed(1)} km/l
                    </span>
                  )}
                </div>

                <button
                  onClick={() => handleDelete(log)}
                  title="Remove fuel log"
                  style={{ background: "none", border: "1px solid #FECACA", borderRadius: 7, width: 32, height: 32, cursor: "pointer", color: "#DC2626", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                ><Trash2 size={13} /></button>
              </div>
            );
          })}
        </div>
      </div>

      {showForm && (
        <FuelLogFormModal
          vehicles={vehicles}
          onClose={() => setShowForm(false)}
          onSubmit={addLog}
        />
      )}
    </main>
  );
}
